import { type BoxRect, type GeometryResolver } from '../types';

/**
 * `GeometryResolver` that measures cells via `data-row-index` / `data-col-index`
 * inside the scroll container. Returns `null` when a cell is not in the DOM; pair
 * with `VirtualGeometryResolver` when rows are virtualized.
 */
export class DomTableGeometryResolver implements GeometryResolver {
  #getContainer: () => HTMLElement | null;

  constructor(getContainer: () => HTMLElement | null) {
    this.#getContainer = getContainer;
  }

  getScrollContainer(): HTMLElement | null {
    return this.#getContainer();
  }

  getCellBounds(rowIndex: number, colIndex: number): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    const cell = container.querySelector(
      `[data-row-index="${rowIndex}"][data-col-index="${colIndex}"]`
    ) as HTMLElement | null;

    if (!cell) {
      return null;
    }

    return this.#getRelativeRect(cell, container);
  }

  getRowBounds(rowIndex: number): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    // Use ANY valid cell in that row to get measurements
    const cell = container.querySelector(
      `[data-row-index="${rowIndex}"]`
    ) as HTMLElement | null;

    if (!cell) {
      return null;
    }

    const cellRect = this.#getRelativeRect(cell, container);
    const horizontal = this.#getDataAreaHorizontalBounds(container);

    if (!horizontal) {
      return null;
    }

    return {
      x: horizontal.x,
      y: cellRect.y,
      width: horizontal.width,
      height: cellRect.height,
    };
  }

  getColumnBounds(colIndex: number): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    // Use ANY valid cell in that column to get measurements
    const cell = container.querySelector(
      `[data-col-index="${colIndex}"]`
    ) as HTMLElement | null;

    if (!cell) {
      return null;
    }

    const cellRect = this.#getRelativeRect(cell, container);
    const vertical = this.#getDataAreaVerticalBounds(container);

    if (!vertical) {
      return null;
    }

    return {
      x: cellRect.x,
      y: vertical.y,
      width: cellRect.width,
      height: vertical.height,
    };
  }

  getContainerBounds(): BoxRect | null {
    const container = this.getScrollContainer();

    if (!container) {
      return null;
    }

    const vertical = this.#getDataAreaVerticalBounds(container);
    const horizontal = this.#getDataAreaHorizontalBounds(container);

    if (!vertical || !horizontal) {
      return null;
    }

    if (horizontal.width === 0 || vertical.height === 0) {
      return null;
    }

    return {
      x: horizontal.x,
      y: vertical.y,
      width: horizontal.width,
      height: vertical.height,
    };
  }

  // ============================================================================
  // PRIVATE DOM MATH HELPERS
  // ============================================================================

  #getRelativeRect(el: HTMLElement, container: HTMLElement): BoxRect {
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate the absolute visual offset from the top-left of the viewport
    let x = elRect.left - containerRect.left;
    let y = elRect.top - containerRect.top;

    // Add the true scroll offset of the container to get the absolute document coordinate
    x += container.scrollLeft;
    y += container.scrollTop;

    return {
      x,
      y,
      width: elRect.width,
      height: elRect.height,
    };
  }

  #getDataAreaVerticalBounds(
    container: HTMLElement
  ): { y: number; height: number } | null {
    // The table element's box covers the header + all data rows (including
    // virtualized gap skeletons). The add-entry / calculate UI lives outside
    // the table as a sibling, so subtracting the header gives us exactly
    // the data-row area — independent of the virtualization strategy.
    //
    // We derive the data-area start from `tableRect.y + headerHeight` instead
    // of `headerRect.y + headerHeight` because the header <tr> is
    // `position: sticky` — its viewport position stays pinned on scroll,
    // which would cause #getRelativeRect to return a scroll-dependent y.
    // The table itself is not sticky, so its relative position is stable.
    const table = container.querySelector('table') as HTMLElement | null;
    const headerRow = container.querySelector('thead > tr') as HTMLElement | null;

    if (!table || !headerRow) {
      return null;
    }

    const tableRect = this.#getRelativeRect(table, container);
    const headerHeight = headerRow.getBoundingClientRect().height;

    return {
      y: tableRect.y + headerHeight,
      height: tableRect.height - headerHeight,
    };
  }

  #getDataAreaHorizontalBounds(
    container: HTMLElement
  ): { x: number; width: number } | null {
    const headerCells = container.querySelectorAll('thead [data-col-index]');

    if (headerCells.length === 0) {
      return null;
    }

    const firstDataCol = headerCells[0] as HTMLElement;
    const lastDataCol = headerCells[headerCells.length - 1] as HTMLElement;

    const firstRect = this.#getRelativeRect(firstDataCol, container);
    const lastRect = this.#getRelativeRect(lastDataCol, container);

    // The entry prefix is `position: sticky` horizontally, so
    // #getRelativeRect would return a scroll-dependent x. Use the first
    // non-sticky data column's stable x and subtract the prefix width.
    const entryPrefix = container.querySelector(
      'thead .entry-number-table-header'
    ) as HTMLElement | null;

    const x = entryPrefix
      ? firstRect.x - entryPrefix.getBoundingClientRect().width
      : firstRect.x;

    return {
      x,
      width: lastRect.x + lastRect.width - x,
    };
  }
}
